import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { adminOrdersService, Order, OrderItem } from '../../services/adminOrders';
import RestaurantSelector from '../../components/RestaurantSelector';
import WaiterShiftIndicator from '../../components/WaiterShiftIndicator';
import ErrorModal from '../../components/ErrorModal';
import ConfirmModal from '../../components/ConfirmModal';
import { ApiError } from '../../services/http';
import { useOrdersRealtime, RealtimeOrder, OrderStatus } from '../../hooks/useOrdersRealtime';
import { useWaiterShift } from '../../context/WaiterShiftContext';
import { tableBillsService, TableBill } from '../../services/tableBills';

// Tipos
interface FrontendOrder {
  id: string;
  tableNumber: number;
  items: OrderItem[];
  totalAmount: number;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  estimatedTime?: number;
  waiterRequested?: boolean;
  specialNotes: string;
  assignedWaiters?: Array<{id: string; name: string}>;
}

// Componente de tarjeta de orden
const WaiterOrderCard = ({ 
  order, 
  onPress,
  onMarkAsServed,
  onConfirmOrder,
  formatCreationTime,
  formatPrice,
}: { 
  order: FrontendOrder; 
  onPress: () => void;
  onMarkAsServed: (orderId: string) => void;
  onConfirmOrder: (orderId: string) => void;
  formatCreationTime: (date: Date) => string;
  formatPrice: (price: number) => string;
}) => {
  const now = new Date();
  const orderAge = now.getTime() - order.createdAt.getTime();
  const minutesWaiting = Math.floor(orderAge / 60000);
  const isOldOrder = minutesWaiting > 10;

  const isCreated = order.status === 'created';
  const statusLabel = isCreated ? 'Por confirmar' : 'Listo';
  const headerText = (order.tableNumber === 0 && order.specialNotes)
    ? order.specialNotes
    : `Mesa ${order.tableNumber}`;

  return (
    <div className="order-card-container p-2 sm:p-3">
      <div 
        className={`order-card ${isCreated ? 'bg-blue-100' : 'bg-white'} rounded-xl border ${isCreated ? 'border-blue-400' : 'border-gray-100'} shadow-md hover:shadow-lg transition-shadow cursor-pointer flex flex-col gap-2 sm:gap-3 px-3 py-2 sm:px-4 sm:py-3 ${isOldOrder ? 'ring-2 ring-orange-200' : ''}`}
        onClick={onPress}
        style={{ minHeight: 'unset', maxWidth: 420, margin: '0 auto' }}
      >
        <div className="order-header flex items-center justify-between gap-2">
          <div className="order-info flex flex-col gap-0.5 min-w-0">
            <div className="order-number text-base sm:text-lg font-semibold text-gray-800 truncate" title={headerText}>
              {headerText}
            </div>
            <div className="order-time text-xs sm:text-sm text-gray-500">{formatCreationTime(order.createdAt)}</div>
          </div>
          <span className={`status-badge px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
            isCreated 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {statusLabel}
          </span>
        </div>
        {isOldOrder && (
          <div className="old-order-badge text-xs text-orange-600 font-semibold flex items-center gap-1">
            <span>⏰ {minutesWaiting} min esperando</span>
          </div>
        )}
        <div className="order-items text-xs sm:text-sm text-gray-700 flex flex-col gap-0.5">
          {(order.items?.length ?? 0) > 0 ? (
            <>
              {order.items.slice(0, 2).map((item, index) => (
                <div key={index} className="order-item truncate">
                  <span className="item-quantity font-semibold">{item.quantity}x </span>
                  {item.name}
                  {item.specialInstructions && (
                    <span className="special-instructions text-gray-400"> ({item.specialInstructions})</span>
                  )}
                </div>
              ))}
              {(order.items?.length ?? 0) > 2 && (
                <div className="more-items text-gray-400">+{(order.items?.length ?? 0) - 2} más...</div>
              )}
            </>
          ) : (
            <div className="no-items text-gray-400">
              Sin items
            </div>
          )}
        </div>
        <div className="order-footer flex items-center justify-between mt-1">
          <div className="order-total text-sm sm:text-base font-bold text-gray-800">{formatPrice(order.totalAmount)}</div>
          <button
            className={`serve-button px-3 py-1 rounded-lg text-white text-xs sm:text-sm font-medium transition disabled:opacity-50 ${
              isCreated 
                ? 'bg-blue-500 hover:bg-blue-600' 
                : 'bg-purple-500 hover:bg-purple-600'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (isCreated) {
                onConfirmOrder(order.id);
              } else {
                onMarkAsServed(order.id);
              }
            }}
          >
            {isCreated ? 'Confirmar' : 'Entregar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function WaiterHome() {
  const { user, isWaiter, isOwner, isManager } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  const { isInTurn } = useWaiterShift();
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState<FrontendOrder | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [waitingForUser, setWaitingForUser] = useState(false);
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; orderId?: string; mode?: 'confirm' | 'deliver' }>({ open: false });
  const [isRefreshing, setIsRefreshing] = useState(false);

  const userIsWaiter = isWaiter() || isOwner() || isManager();
  const userCanAccessWaiters = userIsWaiter;

  // Hook en tiempo real para órdenes - incluir 'created' y 'ready'
  const { orders: realtimeOrders, ordersByStatus, loading, setInitialOrders, refresh } = useOrdersRealtime(['created', 'ready']);
  
  // Estado para órdenes cargadas inicialmente
  const [initialOrdersLoaded, setInitialOrdersLoaded] = useState(false);
  
  // Estados para cuentas de mesa
  const [openBills, setOpenBills] = useState<TableBill[]>([]);
  const [selectedBill, setSelectedBill] = useState<TableBill | null>(null);
  const [showCloseBillModal, setShowCloseBillModal] = useState(false);
  const [loadingBills, setLoadingBills] = useState(false);
  const [activeTab, setActiveTab] = useState<'confirm' | 'ready' | 'bills'>('confirm');
  const [billDetail, setBillDetail] = useState<TableBill | null>(null);
  const [showBillDetailModal, setShowBillDetailModal] = useState(false);
  const [loadingBillDetail, setLoadingBillDetail] = useState(false);

  // Log para debug del estado del turno
  console.log('🏠 WaiterHome - Render:', { 
    isInTurn, 
    ordersCount: realtimeOrders.length, 
    selectedRestaurantId,
    userId: user?.uid,
    waitingForUser,
    userCanAccessWaiters
  });

  // Log adicional para verificar si el valor cambia
  useEffect(() => {
    console.log('🏠 WaiterHome - isInTurn changed to:', isInTurn);
  }, [isInTurn]);



  // Función para cargar órdenes iniciales
  const loadInitialOrders = useCallback(async () => {
    console.log('🏠 WaiterHome - loadInitialOrders ejecutándose');
    console.log('🏠 WaiterHome - user?.uid:', user?.uid);
    console.log('🏠 WaiterHome - selectedRestaurantId:', selectedRestaurantId);
    console.log('🏠 WaiterHome - initialOrdersLoaded:', initialOrdersLoaded);
    
    if (!user?.uid || !selectedRestaurantId || initialOrdersLoaded) {
      console.log('🏠 WaiterHome - Condiciones no cumplidas para cargar órdenes iniciales');
      return;
    }
    
    try {
      console.log('🏠 WaiterHome - Cargando órdenes iniciales para restaurante:', selectedRestaurantId);
      
      // Cargar tanto órdenes created como ready en paralelo
      const [createdOrders, readyOrders] = await Promise.all([
        adminOrdersService.getOrdersByRestaurant(user.uid, selectedRestaurantId, 'created'),
        adminOrdersService.getOrdersByRestaurant(user.uid, selectedRestaurantId, 'ready')
      ]);
      
      const allOrders = [...createdOrders, ...readyOrders];
      console.log('🏠 WaiterHome - Órdenes iniciales recibidas:', allOrders.length);
      console.log('🏠 WaiterHome - Created:', createdOrders.length, 'Ready:', readyOrders.length);
      
      // Convertir órdenes del backend al formato del frontend
      const convertedOrders: FrontendOrder[] = allOrders.map(convertBackendOrderToFrontend);
      
      // Convertir al formato de RealtimeOrder para el hook
      const realtimeOrders: RealtimeOrder[] = convertedOrders.map(order => ({
        id: order.id,
        tableNumber: order.tableNumber,
        items: order.items.map(item => ({
          id: item.id || `item_${Math.random()}`, // Fallback si id es undefined
          name: item.name,
          quantity: item.quantity,
          specialInstructions: item.specialInstructions,
          price: item.price,
        })),
        totalAmount: order.totalAmount,
        status: order.status as OrderStatus, // Convertir string a OrderStatus
        createdAt: order.createdAt,
        updatedAt: order.updatedAt,
        notes: order.specialNotes,
        isPaid: false,
        paymentMethod: '',
        restaurantId: selectedRestaurantId,
        restaurantName: '',
        tableId: '',
        username: '',
        waiterId: '',
      }));
      
      console.log('🏠 WaiterHome - Órdenes convertidas a RealtimeOrder:', realtimeOrders);
      
      // Actualizar el hook con las órdenes iniciales
      console.log('🏠 WaiterHome - Llamando a setInitialOrders...');
      setInitialOrders(realtimeOrders);
      setInitialOrdersLoaded(true);
      
      console.log('🏠 WaiterHome - Órdenes iniciales cargadas exitosamente');
    } catch (error) {
      console.error('🏠 WaiterHome - Error cargando órdenes iniciales:', error);
      const message = error instanceof ApiError && error.message ? error.message : 'No se pudieron cargar las órdenes listas';
      setErrorModal({ open: true, title: 'Error cargando órdenes', message });
    }
  }, [user?.uid, selectedRestaurantId, initialOrdersLoaded, setInitialOrders]);

  // Cargar cuentas abiertas
  const loadOpenBills = useCallback(async () => {
    if (!user?.uid || !selectedRestaurantId) return;
    
    try {
      setLoadingBills(true);
      const { data } = await tableBillsService.getTableBillsByRestaurant(selectedRestaurantId, {
        status: 'open',
        page: 1,
        size: 50
      });
      setOpenBills(data);
    } catch (error) {
      console.error('Error cargando cuentas abiertas:', error);
    } finally {
      setLoadingBills(false);
    }
  }, [user?.uid, selectedRestaurantId]);

  // Cerrar cuenta
  const handleCloseBill = useCallback(async (billId: string) => {
    if (!selectedRestaurantId) return;
    
    try {
      await tableBillsService.closeTableBill(selectedRestaurantId, billId);
      await loadOpenBills();
      setShowCloseBillModal(false);
      setSelectedBill(null);
    } catch (error) {
      console.error('Error cerrando cuenta:', error);
      const message = error instanceof ApiError && error.message ? error.message : 'Error cerrando cuenta';
      setErrorModal({ open: true, title: 'Error', message });
    }
  }, [selectedRestaurantId, loadOpenBills]);

  // Función para refrescar órdenes y cuentas
  const handleRefreshClick = useCallback(async () => {
    try {
      setIsRefreshing(true);
      refresh();
      setInitialOrdersLoaded(false);
      await Promise.all([
        loadInitialOrders(),
        loadOpenBills()
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [refresh, loadInitialOrders, loadOpenBills]);

  // Función para confirmar orden (created -> pending)
  const handleConfirmOrder = useCallback(async (orderId: string) => {
    setConfirmModal({ open: true, orderId, mode: 'confirm' });
  }, []);

  // Función para confirmar orden ahora
  const confirmOrderNow = useCallback(async () => {
    if (!user?.uid || !selectedRestaurantId || !confirmModal.orderId) return;
    
    try {
      await adminOrdersService.updateOrderStatus(
        user.uid,
        selectedRestaurantId,
        confirmModal.orderId,
        { status: 'pending' }
      );
      setConfirmModal({ open: false });
      // Actualización optimista: quitar de la lista "Por confirmar"
      try {
        const updated = (realtimeOrders || []).filter(o => o.id !== confirmModal.orderId);
        setInitialOrders(updated);
      } catch {}
    } catch (error) {
      console.error('Error confirmando orden:', error);
      const message = error instanceof ApiError && error.message ? error.message : 'Error al confirmar orden';
      setErrorModal({ open: true, title: 'Error', message });
      setConfirmModal({ open: false });
    }
  }, [user?.uid, selectedRestaurantId, confirmModal.orderId]);

  // Abrir detalle de cuenta (trae órdenes con items)
  const handleOpenBillDetail = useCallback(async (billId: string) => {
    if (!selectedRestaurantId) return;
    try {
      setLoadingBillDetail(true);
      const detail = await tableBillsService.getTableBillById(selectedRestaurantId, billId);
      setBillDetail(detail);
      setShowBillDetailModal(true);
    } catch (error) {
      console.error('Error cargando detalle de cuenta:', error);
      const message = error instanceof ApiError && error.message ? error.message : 'No se pudo cargar el detalle de la cuenta';
      setErrorModal({ open: true, title: 'Error', message });
    } finally {
      setLoadingBillDetail(false);
    }
  }, [selectedRestaurantId]);

  // Función para convertir orden del backend al formato del frontend
  const convertBackendOrderToFrontend = (backendOrder: Order): FrontendOrder => {
    console.log('🔄 Converting backend order:', backendOrder);
    
    return {
      id: backendOrder.id,
      tableNumber: parseInt(backendOrder.tableNumber) || 0,
      items: backendOrder.items?.map((item) => ({
        id: item.id,
        name: item.name || 'Item sin nombre',
        quantity: item.quantity || 1,
        specialInstructions: item.specialInstructions,
        price: typeof item.price === 'string' ? parseFloat(item.price) : (item.price || 0),
      })) || [],
      totalAmount: typeof backendOrder.totalAmount === 'string' 
        ? parseFloat(backendOrder.totalAmount) 
        : (backendOrder.totalAmount || 0),
      status: backendOrder.status,
      createdAt: new Date(backendOrder.createdAt),
      updatedAt: new Date(backendOrder.updatedAt),
      estimatedTime: backendOrder.estimatedTime || 15,
      waiterRequested: false, // No disponible en el servicio actual
      specialNotes: backendOrder.specialNotes || backendOrder.notes || '',
      assignedWaiters: [], // No disponible en el servicio actual
    };
  };

  // Función para convertir orden en tiempo real al formato del frontend
  const convertRealtimeOrderToFrontend = (realtimeOrder: RealtimeOrder): FrontendOrder => {
    console.log('🔄 Converting realtime order:', realtimeOrder);
    
    return {
      id: realtimeOrder.id,
      tableNumber: realtimeOrder.tableNumber,
      items: realtimeOrder.items?.map((item) => ({
        id: item.id || `item_${Math.random()}`, // Fallback si id es undefined
        name: item.name || 'Item sin nombre',
        quantity: item.quantity || 1,
        specialInstructions: item.specialInstructions,
        price: typeof item.price === 'string' ? parseFloat(item.price) : (item.price || 0),
      })) || [],
      totalAmount: typeof realtimeOrder.totalAmount === 'string' 
        ? parseFloat(realtimeOrder.totalAmount) 
        : (realtimeOrder.totalAmount || 0),
      status: realtimeOrder.status,
      createdAt: new Date(realtimeOrder.createdAt),
      updatedAt: new Date(realtimeOrder.updatedAt),
      estimatedTime: 15, // Valor por defecto
      waiterRequested: false, // No disponible en el servicio actual
      specialNotes: realtimeOrder.notes || '',
      assignedWaiters: [], // No disponible en el servicio actual
    };
  };

  // Efecto para manejar el estado del turno
  useEffect(() => {
    console.log('🔄 useEffect restaurante/usuario - userCanAccessWaiters:', userCanAccessWaiters, 'selectedRestaurantId:', selectedRestaurantId, 'user?.uid:', user?.uid);
    
    // Solo esperar si tenemos acceso y restaurante pero no usuario
    if (userCanAccessWaiters && selectedRestaurantId && !user?.uid) {
      console.log('Restaurante seleccionado pero usuario no disponible aún, esperando...');
      setWaitingForUser(true);
    } else {
      console.log('No esperando usuario');
      setWaitingForUser(false);
    }
  }, [selectedRestaurantId, userCanAccessWaiters, user?.uid]);

  // Efecto para manejar el turno del mesero
  useEffect(() => {
    console.log('🔄 useEffect turno - isInTurn:', isInTurn, 'selectedRestaurantId:', selectedRestaurantId, 'userId:', user?.uid);
    
    // Solo verificar turno si tenemos usuario y restaurante
    if (!selectedRestaurantId || !user?.uid) {
      return;
    }
    
    // Si no está en turno, no esperar, mostrar mensaje apropiado
    if (!isInTurn) {
      console.log('🔄 Turno desactivado');
      setWaitingForUser(false);
    } else {
      console.log('🔄 Turno activado y usuario disponible');
      setWaitingForUser(false);
    }
  }, [isInTurn, selectedRestaurantId, user?.uid]);

  // Efecto para cargar órdenes iniciales cuando esté disponible el usuario y restaurante
  useEffect(() => {
    console.log('🏠 WaiterHome - useEffect loadInitialOrders ejecutándose');
    console.log('🏠 WaiterHome - selectedRestaurantId:', selectedRestaurantId);
    console.log('🏠 WaiterHome - user?.uid:', user?.uid);
    console.log('🏠 WaiterHome - initialOrdersLoaded:', initialOrdersLoaded);
    
    if (selectedRestaurantId && user?.uid && !initialOrdersLoaded) {
      console.log('🏠 WaiterHome - Cargando órdenes iniciales (sin depender de turno)...');
      loadInitialOrders();
    }
  }, [selectedRestaurantId, user?.uid, initialOrdersLoaded, loadInitialOrders]);

  // Cargar cuentas abiertas
  useEffect(() => {
    if (selectedRestaurantId && user?.uid) {
      loadOpenBills();
    }
  }, [selectedRestaurantId, user?.uid, loadOpenBills]);

  // Timeout de seguridad para evitar carga infinita
  useEffect(() => {
    if (waitingForUser) {
      const timeout = setTimeout(() => {
        console.log('⏰ Timeout de seguridad: forzando fin de espera después de 10 segundos');
        setWaitingForUser(false);
      }, 10000); // 10 segundos máximo

      return () => clearTimeout(timeout);
    }
  }, [waitingForUser]);


  const handleOrderPress = (order: FrontendOrder) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const handleMarkAsServed = async (orderId: string) => {
    if (!user?.uid || !selectedRestaurantId) return;
    
    try {
      console.log('🔄 Marcando orden como entregada:', orderId);
      
      const updatedOrder = await adminOrdersService.updateOrderStatus(
        user.uid,
        selectedRestaurantId,
        orderId,
        { status: 'delivered' }
      );
      
      if (updatedOrder) {
        console.log('✅ Orden marcada como entregada exitosamente');
        
        // La orden se actualizará automáticamente en tiempo real
        console.log('✅ Orden marcada como entregada, esperando actualización en tiempo real');
        // Actualización optimista: quitar de la lista actual
        try {
          const updated = (realtimeOrders || []).filter(o => o.id !== orderId);
          setInitialOrders(updated);
        } catch {}
        
        // Opcional: agregar un indicador visual o toast de éxito
      } else {
        console.error('❌ Error actualizando orden');
        setErrorModal({ open: true, title: 'Error al entregar', message: 'Error al marcar la orden como entregada' });
      }
    } catch (error) {
      console.error('❌ Error marcando orden como entregada:', error);
      const message = error instanceof ApiError && error.message ? error.message : 'Error al marcar la orden como entregada';
      setErrorModal({ open: true, title: 'Error al entregar', message });
    }
  };

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  const formatCreationTime = (value: any) => {
    const date = value instanceof Date ? value : (value ? new Date(value) : new Date());
    const time = date.getTime();
    if (Number.isNaN(time)) return '--:--';
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = (hours % 12) || 12;
    return `${displayHours}:${minutes} ${ampm}`;
  };

  // Mostrar loading mientras se valida
  if (loading && realtimeOrders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando órdenes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e9ecef]">
      {/* Header consistente con botón de regreso, selector y shift */}
      <div className="bg-[#22B6B2] dark:bg-[#2C4358] px-4 py-3 sticky top-0 z-30">
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-white hover:text-white/80 hover:bg-white/10 rounded-lg transition-colors text-2xl"
            aria-label="Regresar"
            title="Regresar"
          >
            ←
          </button>
          <div className="flex-1">
            <RestaurantSelector isHeader={true} />
          </div>
          <WaiterShiftIndicator isHeader={true} onReload={handleRefreshClick} isRefreshing={isRefreshing} />
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        {!userCanAccessWaiters ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Acceso Denegado</h2>
            <p className="text-gray-500">No tienes permisos para acceder a la gestión de meseros</p>
          </div>
        ) : !selectedRestaurantId ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🏪</div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Selecciona un Restaurante</h2>
            <p className="text-gray-500 mb-6">
              Para ver las órdenes listas para entregar, primero debes seleccionar un restaurante en el header.
            </p>
            <div className="max-w-sm mx-auto">
              <RestaurantSelector />
            </div>
          </div>
        ) : !user?.uid ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
            <p className="text-gray-600">🔄 Cargando información del usuario...</p>
          </div>
        ) : !isInTurn ? (
          // Mostrar mensaje cuando no está en turno
          <div className="text-center py-20">
            <div className="text-6xl mb-4">⏰</div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No estás en turno</h2>
            <p className="text-gray-500 mb-6">
              Para ver las órdenes listas para entregar, primero debes activar tu turno usando el botón verde en el header.
            </p>
            <div className="max-w-sm mx-auto">
              <WaiterShiftIndicator />
            </div>
          </div>
        ) : (
          <div className="orders-container">
            {/* Tab Navigation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
              <div className="flex flex-wrap items-center p-4 border-b border-gray-200 gap-[5px]">
                <div className="flex flex-wrap items-center gap-[5px]">
                  <button
                    onClick={() => setActiveTab('confirm')}
                    className={`m-0 w-28 sm:w-40 h-11 p-[5px] rounded-lg text-sm font-semibold flex flex-col items-center justify-center transition-all ${
                      activeTab === 'confirm'
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-xl">📝</span>
                    <span className="text-[8px] leading-none">Por confirmar ({ordersByStatus?.created?.length ?? 0})</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('ready')}
                    className={`m-0 w-28 sm:w-40 h-11 p-[5px] rounded-lg text-sm font-semibold flex flex-col items-center justify-center transition-all ${
                      activeTab === 'ready'
                        ? 'bg-green-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-xl">✅</span>
                    <span className="text-[8px] leading-none">Listas ({ordersByStatus?.ready?.length ?? 0})</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('bills')}
                    className={`m-0 w-28 sm:w-40 h-11 p-[5px] rounded-lg text-sm font-semibold flex flex-col items-center justify-center transition-all ${
                      activeTab === 'bills'
                        ? 'bg-purple-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-xl">🧾</span>
                    <span className="text-[8px] leading-none">Cuentas ({openBills?.length ?? 0})</span>
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-4">
                {loading && realtimeOrders.length === 0 && (
                  <div className="text-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando...</p>
                  </div>
                )}

                {/* Tab: Por Confirmar */}
                {activeTab === 'confirm' && (
                  <div className="space-y-4">
                    {(ordersByStatus?.created ?? []).map(realtimeOrder => {
                      const frontendOrder = convertRealtimeOrderToFrontend(realtimeOrder);
                      return (
                        <WaiterOrderCard
                          key={frontendOrder.id}
                          order={frontendOrder}
                          onPress={() => handleConfirmOrder(frontendOrder.id)}
                          onMarkAsServed={handleMarkAsServed}
                          onConfirmOrder={handleConfirmOrder}
                          formatCreationTime={formatCreationTime}
                          formatPrice={formatPrice}
                        />
                      );
                    })}
                    {(ordersByStatus?.created?.length ?? 0) === 0 && (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">📝</div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay órdenes por confirmar</h3>
                        <p className="text-gray-500">Las nuevas órdenes aparecerán aquí</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab: Listas para Entregar */}
                {activeTab === 'ready' && (
                  <div className="space-y-4">
                    {(ordersByStatus?.ready ?? []).map(realtimeOrder => {
                      const frontendOrder = convertRealtimeOrderToFrontend(realtimeOrder);
                      return (
                        <WaiterOrderCard
                          key={frontendOrder.id}
                          order={frontendOrder}
                          onPress={() => handleOrderPress(frontendOrder)}
                          onMarkAsServed={handleMarkAsServed}
                          onConfirmOrder={handleConfirmOrder}
                          formatCreationTime={formatCreationTime}
                          formatPrice={formatPrice}
                        />
                      );
                    })}
                    {(ordersByStatus?.ready?.length ?? 0) === 0 && (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">✅</div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay órdenes listas</h3>
                        <p className="text-gray-500">Las órdenes listas aparecerán aquí</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab: Cuentas Abiertas */}
                {activeTab === 'bills' && (
                  <div>
                    {loadingBills ? (
                      <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-2"></div>
                        <p className="text-gray-600">Cargando cuentas...</p>
                      </div>
                    ) : (openBills?.length ?? 0) === 0 ? (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">🧾</div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay cuentas abiertas</h3>
                        <p className="text-gray-500">Las cuentas activas aparecerán aquí</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {openBills.map((bill) => (
                          <div
                            key={bill.id}
                            className="bg-gray-50 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                                  {bill.tableNumber}
                                </div>
                                <div>
                                  <h3 className="font-semibold text-gray-800">Mesa {bill.tableNumber}</h3>
                                  {bill.waiterName && (
                                    <p className="text-sm text-gray-600">Mesero: {bill.waiterName}</p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold text-green-600">
                                  {formatPrice(bill.totalAmount)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {bill.ordersCount} orden{bill.ordersCount !== 1 ? 'es' : ''}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                              <span>Abierta: {formatCreationTime(new Date(bill.openedAt))}</span>
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                Abierta
                              </span>
                            </div>

                            <button
                              onClick={() => {
                                setSelectedBill(bill);
                                setShowCloseBillModal(true);
                              }}
                              className="w-full bg-red-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                            >
                              <span>🔒</span>
                              <span>Cerrar Cuenta</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Orden */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header del modal */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    Mesa {selectedOrder.tableNumber}
                  </h2>
                  {selectedOrder.assignedWaiters && selectedOrder.assignedWaiters.length > 0 && (
                    <div className="text-sm text-gray-600 mt-1">
                      Meseros: {selectedOrder.assignedWaiters.map(waiter => {
                        const firstName = waiter.name.split(' ')[0];
                        return firstName;
                      }).join(', ')}
                    </div>
                  )}
                  <div className="inline-block px-3 py-1 rounded-full text-sm font-medium text-white mt-2 bg-green-500">
                    ✅ Listo para entregar
                  </div>
                </div>
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>

              {/* Información rápida */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-sm text-gray-500">Total</div>
                  <div className="font-bold text-lg">{formatPrice(selectedOrder.totalAmount)}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-500">Items</div>
                    <div className="font-bold text-lg">{selectedOrder.items?.length ?? 0}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-500">Hora</div>
                  <div className="font-bold text-lg">{formatCreationTime(selectedOrder.createdAt)}</div>
                </div>
              </div>

              {/* Items de la orden */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">Items de la orden</h3>
                <div className="space-y-3">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                          {item.quantity}
                        </div>
                        <div>
                          <div className="font-medium">{item.name}</div>
                          {item.specialInstructions && (
                            <div className="text-sm text-gray-600">Notas: {item.specialInstructions}</div>
                          )}
                        </div>
                      </div>
                      <div className="font-bold text-green-600">{formatPrice(item.price)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedOrder.specialNotes && (
                <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <h3 className="font-semibold text-yellow-800 mb-2">Notas especiales</h3>
                  <p className="text-yellow-700">{selectedOrder.specialNotes}</p>
                </div>
              )}

              {/* Botón de acción */}
              <div className="mt-6">
                <button
                  onClick={() => {
                    handleMarkAsServed(selectedOrder.id);
                    setShowOrderModal(false);
                  }}
                  className="w-full bg-green-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>✅</span>
                  <span>Marcar como Entregado</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .order-card-container {
          margin-bottom: 14px;
        }
        
        .order-card {
          background: #FAFAFA;
          border-radius: 16px;
          padding: 18px;
          border: 1px solid #E2E8F0;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .order-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }
        
        .old-order-card {
          background: #FEF5E7;
          border: 1px solid #F6AD55;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .order-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 12px;
          gap: 8px;
        }
        
        .order-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .order-number-container {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .order-number {
          font-size: 18px;
          font-weight: bold;
          color: #2D3748;
          margin-right: 8px;
        }
        
        .assigned-waiters-badge {
          background-color: #E2E8F0;
          padding: 2px 8px;
          border-radius: 8px;
          align-self: flex-start;
          margin-top: 4px;
          margin-bottom: 2px;
          border: 1px solid #CBD5E0;
        }
        
        .assigned-waiters-text {
          font-size: 13px;
          color: #2D3748;
          font-weight: 500;
        }
        
        .order-time {
          font-size: 14px;
          color: #718096;
          margin-top: 4px;
        }
        
        .status-badge {
          background: #C6F6D5;
          padding: 4px 12px;
          border-radius: 12px;
          border: 1px solid #9AE6B4;
        }
        
        .status-text {
          font-size: 12px;
          font-weight: 600;
          color: #22543D;
        }
        
        .old-order-badge {
          background: #FEF5E7;
          border: 1px solid #F6AD55;
          border-radius: 8px;
          padding: 6px 12px;
          margin-bottom: 12px;
          text-align: center;
        }
        
        .old-order-badge-text {
          font-size: 12px;
          font-weight: 600;
          color: #C05621;
        }
        
        .order-items {
          margin-bottom: 16px;
        }
        
        .order-item {
          font-size: 14px;
          color: #4A5568;
          margin-bottom: 4px;
          padding: 4px 0;
        }
        
        .item-quantity {
          font-weight: 600;
          color: #2D3748;
        }
        
        .special-instructions {
          font-style: italic;
          color: #718096;
        }
        
        .more-items {
          font-size: 12px;
          color: #718096;
          font-style: italic;
          margin-top: 4px;
        }
        
        .no-items {
          font-size: 14px;
          color: #4A5568;
          font-style: italic;
        }
        
        .order-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 12px;
          border-top: 1px solid #E2E8F0;
        }
        
        .order-total {
          font-size: 16px;
          font-weight: bold;
          color: #2D3748;
        }
        
        .serve-button {
          background: #48BB78;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .serve-button:hover {
          background: #38A169;
        }
      `}</style>
      <ErrorModal
        isOpen={errorModal.open}
        title={errorModal.title}
        message={errorModal.message}
        details={errorModal.details}
        onClose={() => setErrorModal({ open: false, message: '' })}
      />
      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.mode === 'confirm' ? 'Confirmar orden' : 'Marcar como entregada'}
        message={confirmModal.mode === 'confirm' ? '¿confirmar orden y pasarla a cocina?' : '¿Deseas marcar esta orden como entregada?'}
        confirmText={confirmModal.mode === 'confirm' ? 'Confirmar' : 'Entregar'}
        confirmButtonClassName={confirmModal.mode === 'confirm' ? 'px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold' : 'px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold'}
        extraActions={confirmModal.mode === 'confirm' ? [
          {
            label: 'Orden sospechosa',
            onClick: () => {
              if (!user?.uid || !selectedRestaurantId || !confirmModal.orderId) return;
              adminOrdersService.updateOrderStatus(user.uid, selectedRestaurantId, confirmModal.orderId, { status: 'cancelled-suspicious' as any })
                .then(() => setConfirmModal({ open: false }))
                .catch((error) => {
                  const message = error instanceof ApiError && error.message ? error.message : 'Error al marcar como sospechosa';
                  setErrorModal({ open: true, title: 'Error', message });
                  setConfirmModal({ open: false });
                });
            },
            className: 'bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100'
          },
          {
            label: 'Orden creada por error',
            onClick: () => {
              if (!user?.uid || !selectedRestaurantId || !confirmModal.orderId) return;
              adminOrdersService.updateOrderStatus(user.uid, selectedRestaurantId, confirmModal.orderId, { status: 'cancelled' as any })
                .then(() => setConfirmModal({ open: false }))
                .catch((error) => {
                  const message = error instanceof ApiError && error.message ? error.message : 'Error al cancelar orden';
                  setErrorModal({ open: true, title: 'Error', message });
                  setConfirmModal({ open: false });
                });
            },
            className: 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
          }
        ] : []}
        onConfirm={confirmOrderNow}
        onCancel={() => setConfirmModal({ open: false })}
      />

      {/* Modal de Confirmación para Cerrar Cuenta */}
      {showCloseBillModal && selectedBill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Cerrar Cuenta - Mesa {selectedBill.tableNumber}
                </h2>
                <button
                  onClick={() => setShowCloseBillModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>

              <div className="mb-6">
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Total de la cuenta:</span>
                    <span className="text-2xl font-bold text-green-600">
                      {formatPrice(selectedBill.totalAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-500">
                    <span>Órdenes: {selectedBill.ordersCount}</span>
                    <span>Abierta: {formatCreationTime(new Date(selectedBill.openedAt))}</span>
                  </div>
                </div>

                <button
                  onClick={() => handleOpenBillDetail(selectedBill.id)}
                  disabled={loadingBillDetail}
                  className={`w-full ${loadingBillDetail ? 'bg-purple-300' : 'bg-purple-500 hover:bg-purple-600'} text-white py-2 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 mb-4`}
                >
                  <span>🧾</span>
                  <span>{loadingBillDetail ? 'Cargando detalle...' : 'Ver detalle de cuenta'}</span>
                </button>

                <p className="text-gray-700 mb-4">
                  ¿Estás seguro de que deseas cerrar esta cuenta? Esta acción liberará la mesa y permitirá que se abra una nueva cuenta.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCloseBillModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleCloseBill(selectedBill.id)}
                  className="flex-1 bg-red-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                >
                  <span>🔒</span>
                  <span>Cerrar Cuenta</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalle de Cuenta */}
      {showBillDetailModal && billDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    Detalles - Mesa {billDetail.tableNumber}
                  </h2>
                  <div className="text-sm text-gray-600">
                    {(billDetail as any).restaurantName || ''}{billDetail.waiterName ? ` · Mesero: ${billDetail.waiterName}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    billDetail.status === 'paid' ? 'bg-emerald-100 text-emerald-700'
                    : billDetail.status === 'closed' ? 'bg-orange-100 text-orange-700'
                    : 'bg-green-100 text-green-700'
                  }`}>
                    {billDetail.status === 'paid' ? 'Pagada' : billDetail.status === 'closed' ? 'Cerrada' : 'Abierta'}
                  </span>
                  <button
                    onClick={() => setShowBillDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                    aria-label="Cerrar"
                    title="Cerrar"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {(() => {
                const toNum = (v: any) => typeof v === 'string' ? parseFloat(v) : (v || 0);
                const computedBillTotal = (billDetail.orders || []).reduce((sum: number, o: any) => {
                  const val = o?.totalAmount ?? o?.total_amount;
                  return sum + toNum(val);
                }, 0);
                const displayTotal = billDetail.totalAmount > 0 ? billDetail.totalAmount : computedBillTotal;
                return (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">Órdenes: {billDetail.orders?.length ?? 0}</div>
                      <div className="text-lg font-bold text-green-600">{formatPrice(displayTotal)}</div>
                    </div>
                    {billDetail.notes && (
                      <div className="mt-2 text-sm text-gray-700">
                        <span className="font-medium">Notas: </span>{billDetail.notes}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="space-y-6">
                {(billDetail.orders || []).map((order: any, idx: number) => {
                  const orderTotal = typeof (order.totalAmount ?? order.total_amount) === 'string'
                    ? parseFloat(order.totalAmount ?? order.total_amount)
                    : (order.totalAmount ?? order.total_amount ?? 0);
                  return (
                    <div key={order.id || idx} className="border border-gray-200 rounded-lg">
                      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="font-semibold text-gray-800">
                            Orden {order.id?.slice?.(0, 6) || (idx + 1)}
                          </div>
                          {order.deliveryAddress && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              {order.deliveryAddress}
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">{formatCreationTime(order.createdAt)}</div>
                          <div className="text-sm text-gray-600">
                            Total: <span className="font-bold text-green-600">{formatPrice(orderTotal)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 space-y-3">
                        {(order.items || []).map((item: any, i: number) => {
                          const qty = item.quantity ?? 1;
                          const priceVal = typeof item.price === 'string' ? parseFloat(item.price) : (item.price ?? 0);
                          const lineTotal = (qty * (priceVal || 0));
                          const name = item.menuItemName ?? item.menu_item_name ?? item.name ?? 'Item';
                          const notes = item.notes ?? item.specialInstructions ?? '';
                          const size = item.size ?? '';
                          return (
                            <div key={item.id || i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                                  {qty}
                                </div>
                                <div>
                                  <div className="font-medium text-gray-800">
                                    {name}{size ? ` (${size})` : ''}
                                  </div>
                                  {notes && (
                                    <div className="text-sm text-gray-600">Notas: {notes}</div>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-500">{formatPrice(priceVal)}</div>
                                <div className="font-bold text-green-600">{formatPrice(lineTotal)}</div>
                              </div>
                            </div>
                          );
                        })}
                        {(order.items || []).length === 0 && (
                          <div className="text-center text-gray-500 text-sm">Sin items</div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {(billDetail.orders || []).length === 0 && (
                  <div className="text-center text-gray-500 text-sm">Esta cuenta aún no tiene órdenes</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 